// TODO - protect this route from direct access

import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { find as _find, get as _get, isEmpty as _isEmpty, some as _some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

// Store
import {
  claimFnolQuery,
  DraftClaim,
  fromClaimFnolActions,
  ReportClaimFnolService
} from '@amfam/claim/fnol/data-access';
import { PolicySelectors } from '@amfam/policy/data-access';
import { AnalyticsFacade, ClaimsAnalytics } from '@amfam/shared/analytics';
import { ClaimsNavigationPaths, Party } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { LoadingSpinnerService, ValidationService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import { ClaimsFnolFormControls } from '../../shared/claims-constants';

@Component({
  selector: 'ds-report-claim-contact-info',
  templateUrl: './report-claim-contact-info.component.html',
  styleUrls: ['./report-claim-contact-info.component.scss'],
  standalone: false
})
export class ReportClaimContactInfoComponent implements OnInit, OnDestroy {
  public claimContactForm: UntypedFormGroup;
  public details: AbstractControl;
  public contactGroup: UntypedFormGroup;
  public draftClaim: DraftClaim;
  public pageTitleName = 'File a Claim';
  private party: Party;
  private claimNumber: string;

  public loading: Observable<Boolean>;

  // Shared Subject for completing all observables on destroy
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private reportClaimFnolService: ReportClaimFnolService,
    private store: Store<fromRoot.RootState>,
    private analyticsFacade: AnalyticsFacade,
    private spinner: LoadingSpinnerService
  ) {}

  ngOnInit() {
    this.spinner.start({
      blockActions: true
    });

    this.buildForm();

    this.loading = this.store.select(claimFnolQuery.getSelectedEntityLoading);
    // Fetch saved draft claim data from report-claim-risk-selection.comp.
    this.store
      .select(userQuery.selectPartyDetails)
      .pipe(takeUntil(this.stop$))
      .subscribe(party => {
        this.party = party;
        this.contactGroup.controls[ClaimsFnolFormControls.PHONE_NUMBER].setValue(
          _get(this.party, 'homePhone.tenDigitPhoneNumber', '') ||
            _get(this.party, 'primaryPhone.tenDigitPhoneNumber', '')
        );
        this.contactGroup.controls[ClaimsFnolFormControls.EMAIL_ADDRESS].setValue(
          _get(this.party, 'primaryEmail.emailAddress', '')
        );
      });

    combineLatest(
      this.store.select(claimFnolQuery.getClaimFnolSelectedEntity),
      this.store.select(PolicySelectors.selectPolicies),
      (draftClaim, policies) => {
        return {
          draftClaim: draftClaim,
          policies: policies
        };
      }
    )
      .pipe(
        takeUntil(this.stop$),
        filter(state => {
          return state && !_some(state, { loading: true as any });
        })
      )
      .subscribe(state => {
        this.draftClaim = state.draftClaim;
        this.claimNumber = this.draftClaim.claimNumber;
        this.spinner.stop();
        if (this.contactGroup.valid) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [ClaimsNavigationPaths.CLAIM_SUMMARY]
            })
          );
        }

        /**
         * Condition to check required data is available to populate on landing from edit-risk-summary or via back button.
         */
        if (this.draftClaim && !!this.draftClaim.fnolDetail.whatHappened) {
          this.contactGroup.controls[ClaimsFnolFormControls.WHAT_HAPPENED].setValue(
            this.draftClaim.fnolDetail.whatHappened
          );
        }
        if (_get(this.draftClaim, 'insured.emails[0].emailAddress')) {
          const emailAddress = this.draftClaim.insured.emails[0].emailAddress;
          this.contactGroup.controls[ClaimsFnolFormControls.EMAIL_ADDRESS].setValue(emailAddress);
        }

        const phones = _get(this.draftClaim, 'insured.phones', []);
        const cellPhoneType = _find(phones, { phoneType: 'Cell' });
        if (!_isEmpty(cellPhoneType)) {
          const phoneNumber =
            _get(cellPhoneType, 'areaCode', '') + _get(cellPhoneType, 'number', '');
          this.contactGroup.controls[ClaimsFnolFormControls.PHONE_NUMBER].setValue(phoneNumber);
        } else if (_get(this.draftClaim, 'insured.phones[0]', '')) {
          const phoneNumberObj = this.draftClaim.insured.phones[0];
          const phoneNumber =
            _get(phoneNumberObj, 'areaCode', '') + _get(phoneNumberObj, 'number', '');
          this.contactGroup.controls[ClaimsFnolFormControls.PHONE_NUMBER].setValue(phoneNumber);
        }
        if (state.policies) {
          this.reportClaimFnolService.setClaimsPageTitle(
            state.policies.filter(policy => policy.policyNumber === this.draftClaim.policyNumber)[0]
          );
        }
      });
    this.analyticsFacade.trackPage(ClaimsAnalytics.pageAutoClaimDetails);
  }

  // onDestroy emit value and complete
  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  buildForm() {
    this.claimContactForm = this.formBuilder.group({
      contactInfo: this.formBuilder.group({
        whatHappened: [
          '',
          Validators.compose([
            Validators.required,
            Validators.maxLength(250),
            ValidationService.nonAsciiCharacters
          ])
        ],
        /**
         * Validations absorbed from forgot-password-component -> email, billing-contact -> phone number
         * Below validators also satisfies fnol email, phone number validation reqs.
         */
        emailAddress: [
          '',
          Validators.compose([
            Validators.maxLength(50),
            ValidationService.emailValidator,
            ValidationService.allowedEmailCharactersValidator
          ])
        ],
        phoneNumber: [
          '',
          Validators.compose([Validators.required, ValidationService.phoneNumberValidator])
        ]
      })
    });

    this.contactGroup = <UntypedFormGroup>this.claimContactForm.controls['contactInfo'];
  }

  goBackOneStep() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_CAUSE_PATH]
      })
    );
  }

  cancel() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_HOME_PATH]
      })
    );
  }

  /**
   * 1. Check claimNumber exists on draftClaim node level.
   *   1.2.1 Available - proceed with update FNOL operation
   *   1.2.2 Un Available - Do create FNOL operation and on ..
   *    1.2.2.1 Success - proceed with update FNOL operation
   *    1.2.2.2 Fail - Redirect to Summary component(because update operation needs claimNumber).
   */

  submitClaimContact(): void {
    // Must use Object.assign to avoid Store error
    const updateClaimRequestObj = this.reportClaimFnolService.createUpdateDraftClaimInfoFactory(
      this.contactGroup.value,
      Object.assign({}, this.draftClaim)
    );
    if (!this.claimNumber) {
      this.spinner.start({
        blockActions: true
      });
      const draftClaim: DraftClaim = Object.assign({}, this.draftClaim, updateClaimRequestObj.fnol);
      this.store.dispatch(new fromClaimFnolActions.CreateDraftClaim(draftClaim));
    } else {
      this.store.dispatch(new fromClaimFnolActions.UpdateDraftClaim(updateClaimRequestObj));
      this.store.dispatch(
        fromRouterActions.Go({
          path: [ClaimsNavigationPaths.CLAIM_SUMMARY]
        })
      );
    }
  }
}
